import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiService from '../../../../services/api';

// ── Helpers — identical to UserCommunications ─────────────────────────────
const getInitials = (email = '') => email.slice(0, 2).toUpperCase();

const timeAgo = (dateString) => {
  if (!dateString) return '';
  const diff = Date.now() - new Date(dateString).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)  return `${d}d ago`;
  return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const formatFull = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const AVATAR_COLORS = [
  '#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444',
  '#8b5cf6','#ec4899','#14b8a6','#f97316','#06b6d4',
];
const avatarColor = (email = '') => {
  let hash = 0;
  for (let c of email) hash = (hash * 31 + c.charCodeAt(0)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const STATUS_META = {
  active:    { bg: '#dcfce7', color: '#16a34a', label: 'Active'    },
  closed:    { bg: '#dbeafe', color: '#2563eb', label: 'Closed'    },
  escalated: { bg: '#fee2e2', color: '#dc2626', label: 'Escalated' },
};

// ── Avatars — matching UserCommunications exactly ─────────────────────────
const Avatar = ({ email, size = 36 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%',
    background: avatarColor(email),
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'white', fontWeight: 700, fontSize: size * 0.35,
    flexShrink: 0, letterSpacing: '-0.5px',
  }}>
    {getInitials(email)}
  </div>
);

const BotAvatar = ({ size = 36 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%',
    background: 'linear-gradient(135deg, #0d9488, #6366f1)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'white', fontSize: size * 0.42, flexShrink: 0,
  }}>🤖</div>
);

// Bold/newline formatter
function FormattedText({ text }) {
  return (
    <span>
      {(text || '').split('\n').map((line, i) => (
        <React.Fragment key={i}>
          {i > 0 && <br />}
          {line.split(/(\*\*[^*]+\*\*)/).map((part, j) =>
            part.startsWith('**') && part.endsWith('**')
              ? <strong key={j}>{part.slice(2, -2)}</strong>
              : part
          )}
        </React.Fragment>
      ))}
    </span>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
function ChatBotView() {
  const [sessions,     setSessions]     = useState([]);
  const [stats,        setStats]        = useState({ total: 0, active: 0, closed: 0, escalated: 0 });
  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSess, setSelectedSess] = useState(null);
  const [messages,     setMessages]     = useState([]);
  const [loadingMsgs,  setLoadingMsgs]  = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [acting,       setActing]       = useState(false);
  const chatEndRef = useRef(null);

  // ── Resizable column — identical pattern to UserCommunications ────────────
  const [col1Width,    setCol1Width]    = useState(340);
  const dragStart      = useRef(0);
  const dragStartWidth = useRef(0);

  const onDividerMouseDown = (e) => {
    e.preventDefault();
    dragStart.current      = e.clientX;
    dragStartWidth.current = col1Width;
    document.body.style.cursor     = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      const delta = ev.clientX - dragStart.current;
      setCol1Width(Math.max(200, Math.min(540, dragStartWidth.current + delta)));
    };
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Fetch sessions ─────────────────────────────────────────────────────────
  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const res = await apiService.adminGetChatSessions();
      if (res.success) {
        const list = res.sessions || [];
        setSessions(list);
        setStats({
          total:     list.length,
          active:    list.filter(s => s.status === 'active').length,
          closed:    list.filter(s => s.status === 'closed').length,
          escalated: list.filter(s => s.status === 'escalated').length,
        });
      } else throw new Error(res.error || 'Failed to fetch');
    } catch (err) { setError(err.message); setSessions([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // ── Fetch messages ─────────────────────────────────────────────────────────
  const fetchMessages = async (sess) => {
    setSelectedSess(sess);
    setMessages([]);
    setLoadingMsgs(true);
    try {
      const res = await apiService.adminGetChatSessionMessages(sess.id);
      if (res.success) setMessages(res.messages || []);
    } catch (e) { console.warn('Could not load messages:', e); }
    finally { setLoadingMsgs(false); }
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleEscalate = async () => {
    if (!selectedSess || acting) return;
    if (!window.confirm('Mark this session as escalated?')) return;
    setActing(true);
    try {
      const res = await apiService.adminEscalateChatSession(selectedSess.id);
      if (res.success) {
        const updated = { ...selectedSess, status: 'escalated' };
        setSelectedSess(updated);
        setSessions(prev => prev.map(s => s.id === selectedSess.id ? { ...s, status: 'escalated' } : s));
      }
    } finally { setActing(false); }
  };

  const handleClose = async () => {
    if (!selectedSess || acting) return;
    if (!window.confirm('Mark this session as resolved?')) return;
    setActing(true);
    try {
      const res = await apiService.adminCloseChatSession(selectedSess.id);
      if (res.success) {
        const updated = { ...selectedSess, status: 'closed' };
        setSelectedSess(updated);
        setSessions(prev => prev.map(s => s.id === selectedSess.id ? { ...s, status: 'closed' } : s));
      }
    } finally { setActing(false); }
  };

  const handleExport = () => {
    if (!selectedSess) return;
    const lines = [
      `AI Chat Session #${selectedSess.id}`,
      `User: ${selectedSess.user_email}`,
      `Topic: ${selectedSess.topic}`,
      `Status: ${selectedSess.status}`,
      `Started: ${formatFull(selectedSess.started_at)}`,
      '',
      ...messages.map(m => `[${formatFull(m.created_at)}] ${m.role.toUpperCase()}: ${m.content}`),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `chat-session-${selectedSess.id}.txt`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ── Filtered ───────────────────────────────────────────────────────────────
  const filtered = sessions.filter(s => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      s.user_email?.toLowerCase().includes(q) ||
      s.topic?.toLowerCase().includes(q) ||
      s.last_user_message?.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchSearch && matchStatus;
  });

  // ── Styles — verbatim from UserCommunications S object ────────────────────
  const S = {
    root:         { display: 'flex', height: '100vh', background: '#f8fafc', fontFamily: "'DM Sans', sans-serif", overflow: 'hidden' },
    left:         { width: col1Width, flexShrink: 0, display: 'flex', flexDirection: 'column', background: 'white', borderRight: '1px solid #e2e8f0', transition: 'none' },
    leftHeader:   { padding: '20px 20px 0', borderBottom: '1px solid #f1f5f9' },
    title:        { fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '0 0 4px' },
    subtitle:     { fontSize: 12, color: '#94a3b8', margin: '0 0 16px' },
    statsRow:     { display: 'flex', gap: 8, marginBottom: 16 },
    statChip:     (color) => ({ flex: 1, background: '#f8fafc', borderRadius: 8, padding: '8px 6px', textAlign: 'center', borderTop: `3px solid ${color}` }),
    searchWrap:   { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 8 },
    searchInput:  { flex: 1, padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, outline: 'none', background: '#f8fafc' },
    select:       { padding: '9px 10px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 12, background: '#f8fafc', cursor: 'pointer', color: '#475569' },
    list:         { flex: 1, overflowY: 'auto' },
    groupRow:     (active) => ({
      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px',
      cursor: 'pointer', borderBottom: '1px solid #f8fafc',
      background: active ? '#f0f4ff' : 'white',
      borderLeft: active ? '3px solid #6366f1' : '3px solid transparent',
      transition: 'all 0.15s',
    }),
    avatarStack:  { position: 'relative', width: 44, height: 36, flexShrink: 0 },
    groupInfo:    { flex: 1, minWidth: 0 },
    groupUsers:   { fontSize: 13, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    groupMeta:    { fontSize: 11, color: '#94a3b8', marginTop: 2 },
    groupPreview: { fontSize: 12, color: '#64748b', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    right:        { flex: 1, display: 'flex', flexDirection: 'column', background: '#f8fafc' },
    emptyState:   { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' },
    chat:         { flex: 1, display: 'flex', flexDirection: 'column' },
    chatMessages: { flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 3, background: '#e5ddd5' },
    msgBubble:    (isBot) => ({
      maxWidth: '72%', minWidth: 130,
      background: isBot ? '#ffffff' : '#dcf8c6',
      color: '#111b21', padding: '7px 10px 5px 10px',
      borderRadius: isBot ? '0px 10px 10px 10px' : '10px 0px 10px 10px',
      boxShadow: '0 1px 1px rgba(0,0,0,0.13)', position: 'relative',
    }),
    chatActions:  { padding: '12px 20px', background: 'white', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 10 },
    actionBtn:    (bg, color, disabled = false) => ({
      flex: 1, padding: '9px 12px',
      background: disabled ? '#f1f5f9' : bg,
      color: disabled ? '#94a3b8' : color,
      border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
    }),
  };

  return (
    <div style={S.root}>

      {/* ── Left: Session list ─────────────────────────────────────────────── */}
      <div style={S.left}>
        <div style={S.leftHeader}>
          <h2 style={S.title}>🤖 AI Chat Sessions</h2>
          <p style={S.subtitle}>
            Bot ↔ user conversations · {filtered.length} session{filtered.length !== 1 ? 's' : ''}
          </p>

          <div style={S.statsRow}>
            {[
              { label: 'Total',     value: stats.total,     color: '#6366f1' },
              { label: 'Active',    value: stats.active,    color: '#16a34a' },
              { label: 'Closed',    value: stats.closed,    color: '#2563eb' },
              { label: 'Escalated', value: stats.escalated, color: '#ef4444' },
            ].map(s => (
              <div key={s.label} style={S.statChip(s.color)}>
                <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={S.searchWrap}>
          <input
            style={S.searchInput}
            placeholder="Search user, topic, message..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select style={S.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
            <option value="escalated">Escalated</option>
          </select>
        </div>

        <div style={S.list}>
          {loading && (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              Loading sessions…
            </div>
          )}
          {error && !loading && (
            <div style={{ padding: 16, background: '#fee2e2', color: '#dc2626', fontSize: 12, margin: 12, borderRadius: 8 }}>
              {error}
              <button onClick={fetchSessions} style={{ marginLeft: 8, textDecoration: 'underline', background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}>
                Retry
              </button>
            </div>
          )}

          {!loading && !error && filtered.map(sess => {
            const isActive = selectedSess?.id === sess.id;
            const sm = STATUS_META[sess.status] || STATUS_META.active;
            return (
              <div key={sess.id} style={S.groupRow(isActive)} onClick={() => fetchMessages(sess)}>
                {/* Avatar stack: user + bot — mirrors buyer/seller in UserCommunications */}
                <div style={S.avatarStack}>
                  <div style={{ position: 'absolute', top: 0, left: 0 }}>
                    <Avatar email={sess.user_email} size={30} />
                  </div>
                  <div style={{ position: 'absolute', top: 8, left: 16, border: '2px solid white', borderRadius: '50%' }}>
                    <BotAvatar size={26} />
                  </div>
                </div>

                <div style={S.groupInfo}>
                  <div style={S.groupUsers}>
                    {sess.user_email?.split('@')[0]}
                    <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6, fontSize: 11 }}>
                      ↔ 🤖 Bot
                    </span>
                  </div>
                  <div style={S.groupMeta}>
                    {sess.message_count || 0} msg{(sess.message_count || 0) !== 1 ? 's' : ''} ·{' '}
                    <span style={{ fontWeight: 600, color: '#475569' }}>{sess.topic || 'General'}</span>
                  </div>
                  {sess.last_user_message && (
                    <div style={S.groupPreview}>{sess.last_user_message}</div>
                  )}
                </div>

                <div style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0, marginTop: 2, textAlign: 'right' }}>
                  {timeAgo(sess.last_message_at)}
                  <div style={{ marginTop: 4 }}>
                    <span style={{ background: sm.bg, color: sm.color, padding: '1px 6px', borderRadius: 20, fontSize: 9, fontWeight: 700 }}>
                      {sm.label.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {!loading && !error && filtered.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              No sessions found.
            </div>
          )}
        </div>
      </div>

      {/* ── Divider — identical to UserCommunications ─────────────────────── */}
      <div
        onMouseDown={onDividerMouseDown}
        style={{ width: 5, flexShrink: 0, cursor: 'col-resize', background: 'transparent', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        title="Drag to resize"
      >
        <div style={{ width: 3, height: 40, borderRadius: 3, background: '#cbd5e1' }} />
      </div>

      {/* ── Right: Chat view ───────────────────────────────────────────────── */}
      {!selectedSess ? (
        <div style={{ ...S.right, ...S.emptyState }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🤖</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#334155' }}>Select a session</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>
            Choose a bot chat session from the left to view the full conversation
          </div>
        </div>
      ) : (
        <div style={S.chat}>

          {/* ── WhatsApp-style chat header — matches UserCommunications exactly ── */}
          <div style={{ padding: '10px 16px', background: '#075e54', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ position: 'relative', width: 42, height: 38, flexShrink: 0 }}>
                <div style={{ position: 'absolute', top: 0, left: 0 }}>
                  <Avatar email={selectedSess.user_email} size={28} />
                </div>
                <div style={{ position: 'absolute', top: 10, left: 16, border: '2px solid #075e54', borderRadius: '50%' }}>
                  <BotAvatar size={24} />
                </div>
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>
                  {selectedSess.user_email?.split('@')[0]} &amp; AI Assistant
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 1 }}>
                  Topic: {selectedSess.topic || 'General'} · {messages.length || selectedSess.message_count || 0} messages
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {(() => {
                const sm = STATUS_META[selectedSess.status] || STATUS_META.active;
                return (
                  <span style={{ background: 'rgba(255,255,255,0.15)', color: 'white', padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, border: '1px solid rgba(255,255,255,0.3)' }}>
                    ● {sm.label}
                  </span>
                );
              })()}
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>
                Started {formatFull(selectedSess.started_at)}
              </div>
            </div>
          </div>

          {/* ── Messages ── */}
          <div style={S.chatMessages}>
            {loadingMsgs && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}>
                <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 20, padding: '8px 18px', fontSize: 12, color: '#667781', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                  Loading messages…
                </div>
              </div>
            )}

            {!loadingMsgs && messages.length === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 10 }}>
                <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 12, padding: '20px 28px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', maxWidth: 340 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111b21', marginBottom: 6 }}>No messages recorded</div>
                  <div style={{ fontSize: 12, color: '#667781', lineHeight: 1.5 }}>
                    Session has <strong>{selectedSess.message_count || 0}</strong> message(s) in the database.
                  </div>
                </div>
              </div>
            )}

            {!loadingMsgs && messages.length > 0 && (() => {
              const items = [];
              let lastDate = null;
              messages.forEach((msg, i) => {
                const msgDate   = msg.created_at ? new Date(msg.created_at) : null;
                const shortDate = msgDate?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                const timeStr   = msgDate ? formatTime(msg.created_at) : '';
                const isBot     = msg.role === 'bot';
                const prevMsg   = messages[i - 1];
                const senderChanged = i === 0 || prevMsg?.role !== msg.role;

                // Date separator
                if (shortDate && shortDate !== lastDate) {
                  lastDate = shortDate;
                  items.push(
                    <div key={`date-${i}`} style={{ display: 'flex', justifyContent: 'center', margin: '10px 0 6px' }}>
                      <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 8, padding: '4px 12px', fontSize: 11, color: '#667781', boxShadow: '0 1px 1px rgba(0,0,0,0.1)', fontWeight: 600 }}>
                        {shortDate}
                      </div>
                    </div>
                  );
                }

                items.push(
                  <div
                    key={msg.id || i}
                    style={{
                      display: 'flex',
                      justifyContent: isBot ? 'flex-start' : 'flex-end',
                      marginBottom: 4,
                      paddingLeft:  isBot ? 0  : 48,
                      paddingRight: isBot ? 48 : 0,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, flexDirection: isBot ? 'row' : 'row-reverse', maxWidth: '100%' }}>
                      <div style={{ flexShrink: 0, marginBottom: 4 }}>
                        {isBot ? <BotAvatar size={28} /> : <Avatar email={selectedSess.user_email} size={28} />}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: isBot ? 'flex-start' : 'flex-end' }}>
                        {senderChanged && (
                          <div style={{
                            fontSize: 11, fontWeight: 700, marginBottom: 2,
                            color: isBot ? '#0d9488' : avatarColor(selectedSess.user_email),
                            paddingLeft: isBot ? 2 : 0, paddingRight: isBot ? 0 : 2,
                          }}>
                            {isBot ? '🤖 AI Assistant' : `${selectedSess.user_email?.split('@')[0]} · User`}
                          </div>
                        )}
                        <div style={S.msgBubble(isBot)}>
                          {/* Chat tail */}
                          <div style={{
                            position: 'absolute', top: 0,
                            [isBot ? 'left' : 'right']: -6,
                            width: 0, height: 0, borderStyle: 'solid',
                            borderWidth: isBot ? '0 8px 8px 0' : '0 0 8px 8px',
                            borderColor: isBot
                              ? 'transparent #ffffff transparent transparent'
                              : 'transparent transparent transparent #dcf8c6',
                          }} />
                          <div style={{ fontSize: 13.5, lineHeight: 1.6, color: '#111b21', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                            <FormattedText text={msg.content} />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3, marginTop: 3 }}>
                            <span style={{ fontSize: 10, color: '#667781', whiteSpace: 'nowrap' }}>{timeStr}</span>
                            {!isBot && (
                              <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
                                <path d="M1 6l3 3 5-5" stroke="#8696a0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M5 6l3 3 5-5" stroke="#8696a0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              });
              return items;
            })()}
            <div ref={chatEndRef} />
          </div>

          {/* ── Actions — identical to UserCommunications ── */}
          <div style={S.chatActions}>
            <button
              style={S.actionBtn('#fee2e2', '#dc2626', acting || selectedSess.status === 'escalated')}
              onClick={handleEscalate}
              disabled={acting || selectedSess.status === 'escalated'}
            >
              🚩 {selectedSess.status === 'escalated' ? 'Escalated' : 'Escalate'}
            </button>
            <button
              style={S.actionBtn('#dcfce7', '#16a34a', acting || selectedSess.status === 'closed')}
              onClick={handleClose}
              disabled={acting || selectedSess.status === 'closed'}
            >
              ✓ {selectedSess.status === 'closed' ? 'Resolved' : 'Mark Resolved'}
            </button>
            <button style={S.actionBtn('#f1f5f9', '#475569')} onClick={handleExport}>
              📥 Export
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatBotView;